import { useEffect, useState } from 'react'
import { AppLayout } from '../../App/Layout/AppLayout'
import { useDispatch, useSelector } from 'react-redux'
import { setActiveNavigationTab } from '../../store/slices/navigation'
import { NAVIGATION_TAB_NAMES } from '../../types/navigation'
import { Box, Button, Flex, Stack, Text, Wrap } from '@homebotapp/hb-react-component-catalog'
import { ListingCardNew } from '../../Components/homeSearch/ListingCardNew/ListingCardNew'

import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { Link, useLocation } from 'react-router-dom'
import { FeedBase, FeedType, ListingCompact } from '../../api/gqlaxy/gql/generated/graphql'
import { useGetFeed } from '../../hooks/gallery/useFeed'
import { useRecoilState } from 'recoil'
import { currentSelectedLocation } from '../../Components/homeSearch/GoogleAutoComplete/state/currentSelectedLocation'
import { GallerySubscribeModal } from '../../Components/homeSearch/GallerySubscribeModal/GallerySubscribeModal'
import { useToggleFeedSubscribed } from '../../hooks/gallery/useToggleFeedSubscribed'
import LoadingPanel from '../../Components/shared/Loading/LoadingBlock'
import { GoogleAutoComplete } from '../../Components/homeSearch/GoogleAutoComplete/GoogleAutoComplete'
import { useChangeFeedLocation } from '../../hooks/gallery/useChangeFeedLocation'
import { HomeSearchFooter } from '../../Components/homeSearch/HomeSearchFooter/HomeSearchFooter'
import { selectNativeAccessFeature } from '../../store/selectors/customerProfile'
import { RootState } from '../../types/rootState'
import useViewport from '../../hooks/useViewport'
import { DownloadMobileAppPromptCard } from '../../Components/homeSearch/ListingDetails/DownloadMobileAppPromptCard/Desktop/DownloadMobileAppPromptCard'
import { MobileDownloadMobileAppPromptCard } from '../../Components/homeSearch/ListingDetails/DownloadMobileAppPromptCard/Mobile/MobileDownloadMobileAppPromptCard'

export const MSG = defineMessages({
  subscribe: {
    id: 'Gallery.subscribe',
    defaultMessage: 'Subscribe'
  },
  subscribed: {
    id: 'Gallery.subscribed',
    defaultMessage: 'Subscribed'
  },
  homes: {
    id: 'Gallery.homes',
    defaultMessage: 'Homes'
  },
  updatedToday: {
    id: 'Gallery.updatedToday',
    defaultMessage: 'Updated today - Curated by Homebot'
  },
  recentPriceDrop: {
    id: 'Gallery.priceDrops',
    defaultMessage: 'Price Drops'
  },
  recentPriceDropDescription: {
    id: 'Gallery.priceDropsDescription',
    defaultMessage: 'See places with a reduced asking price'
  },
  savedSearch: {
    id: 'Gallery.savedSearch',
    defaultMessage: 'Saved Search'
  },
  favorites: {
    id: 'Gallery.favorites',
    defaultMessage: 'Favorites'
  },
  recentlySold: {
    id: 'Gallery.recentlySold',
    defaultMessage: 'Recently Sold'
  },
  recentlySoldDescription: {
    id: 'Gallery.recentlySoldDescription',
    defaultMessage: 'Explore to see what the market is like'
  },
  yourStuff: {
    id: 'Gallery.yourStuff',
    defaultMessage: 'Your Stuff'
  },
  localGallery: {
    id: 'Gallery.localGallery',
    defaultMessage: 'Local Gallery'
  },
  forYou: {
    id: 'Gallery.forYou',
    defaultMessage: 'For You'
  },
  help: {
    id: 'Gallery.help',
    defaultMessage: 'Helping you find your next home'
  },
  localGalleries: {
    id: 'Gallery.localGalleries',
    defaultMessage: 'Local Galleries'
  },
  changeLocation: {
    id: 'Gallery.changeLocation',
    defaultMessage: 'Change Location'
  }
})

export const ForYouGallery = ({ match, isPublic }) => {
  const dispatch = useDispatch()
  const location = useLocation<{
    feed: FeedBase
  }>()
  const intl = useIntl()
  const { isMediumOrAbove } = useViewport()
  const hasNativeAccess = useSelector<RootState, boolean>(selectNativeAccessFeature)
  const { customerProfileId, galleryId } = match.params
  const { data, refetch, isLoading: loadingFeed } = useGetFeed({ feedId: galleryId })
  const [showSubscribedModal, setShowSubscribedModal] = useState(false)
  const [showGooglePlaceSelector, setShowGooglePlaceSelector] = useState(false)

  const { mutateAsync: changeFeedLocation, isLoading: changingFeedLocation } = useChangeFeedLocation()

  const { mutateAsync: toggleSub, isLoading: togglingSub } = useToggleFeedSubscribed()

  const [_, setCurrentLocation] = useRecoilState(currentSelectedLocation)

  useEffect(() => {
    window.scrollTo(0, 0)
  }, [])

  useEffect(() => {
    dispatch(setActiveNavigationTab(NAVIGATION_TAB_NAMES.FORYOU))
  }, [dispatch])

  const onLocationChange = async (place: { place_id: string }) => {
    setCurrentLocation(place)
    setShowGooglePlaceSelector(false)
    await changeFeedLocation({ feedId: galleryId, googlePlaceId: place.place_id })
    await refetch()
  }

  const toggleSubscription = async () => {
    await toggleSub({ feedId: galleryId })
    await refetch()
    if (data?.feed.subscribed) {
      return
    }
    setShowSubscribedModal(true)
  }

  if (togglingSub || loadingFeed || changingFeedLocation) {
    return (
      <Box position='absolute' top='50%' left='50%'>
        <LoadingPanel />
      </Box>
    )
  }

  return (
    <AppLayout loading={false} enableAgentDropdown match={match}>
      {showGooglePlaceSelector && (
        <GoogleAutoComplete onClose={() => setShowGooglePlaceSelector(false)} onPlaceSelected={onLocationChange} />
      )}
      {showSubscribedModal && (
        <GallerySubscribeModal
          feedCategory={data?.feed.feedType as FeedType}
          onClose={() => {
            setShowSubscribedModal(false)
          }}
        />
      )}
      <Box pt={10} px={[5, 20]}>
        <Flex pb={10} alignItems='center' justifyContent='space-between'>
          <Box>
            <Text m={0} fontWeight={600} size={'xl'}>
              <FormattedMessage id={MSG.forYou.id} defaultMessage={MSG.forYou.defaultMessage} />
            </Text>
            <Text m={0} size='xs'>
              <FormattedMessage id={MSG.help.id} defaultMessage={MSG.help.defaultMessage} />
            </Text>
          </Box>

          <Flex direction={['column', 'row']} alignItems='center'>
            {/* <Text textAlign='right' size='xs' m={0}>
              {MSG.localGalleries.defaultMessage} for {currentLocation.description || 'Denver, CO'}
            </Text> */}
            {/* <Text
              onClick={() => {
                setShowGooglePlaceSelector(true)
              }}
              cursor='pointer'
              textColor='primary.500'
              float='right'
              size='xs'
              m={0}
              mb={[3, 0]}
            >
              <FormattedMessage id={MSG.changeLocation.id} defaultMessage={MSG.changeLocation.defaultMessage} />
            </Text> */}
            <Button
              onClick={() => {
                toggleSubscription()
              }}
              tracking={{
                guid: data?.feed.subscribed ? '6NU8QA8FBwaqxj40PgLWEN' : '6NU8QA8FBwaqxj30PgLSUB',
                ui_context: 'GALLERY_SUBSCRIBE_BUTTON',
                descriptor: data?.feed.subscribed
                  ? `Subscribe to ${data?.feed.feedType} feed`
                  : `Unsubscribe from ${data?.feed.feedType} feed`
              }}
              size={['sm', 'md']}
              ml={[0, 3]}
              colorScheme={data?.feed.subscribed ? 'secondary' : 'primary'}
            >
              <Text size='md' textColor={data?.feed.subscribed ? 'primary.500' : 'black'} fontWeight={600} m={0}>
                {data?.feed.subscribed ? (
                  <FormattedMessage id={MSG.subscribed.id} defaultMessage={MSG.subscribed.defaultMessage} />
                ) : (
                  <FormattedMessage id={MSG.subscribe.id} defaultMessage={MSG.subscribe.defaultMessage} />
                )}
              </Text>
            </Button>
          </Flex>
        </Flex>

        <Stack
          borderWidth={1}
          flex={1}
          borderStyle='solid'
          bg='neutral.100'
          direction='row'
          py={3}
          mx={-20}
          px={20}
          alignItems='center'
          spacing={1}
        >
          <Link to={`/for-you/${customerProfileId}`}>
            <Text m={0} fontWeight={600} size='md' textColor='primary.500'>
              <FormattedMessage id={MSG.forYou.id} defaultMessage={MSG.forYou.defaultMessage} />
            </Text>
          </Link>
          <Text m={0} size='xs' textColor='neutral.500'>
            {'>'}
          </Text>
          <Text m={0} fontWeight={600} textColor='white' size='xs'>
            {data?.feed.feedType ? intl.formatMessage(MSG[data?.feed.feedType]) : ''}
          </Text>
        </Stack>

        <Box mt={10}>
          <Text m={0} mb={1} mr={1} fontWeight={600} size='md'>
            {data?.feed?.listings.length}{' '}
            <FormattedMessage id={MSG.homes.id} defaultMessage={MSG.homes.defaultMessage} />
          </Text>
          <Text m={0} size='sm' textColor='neutral.500'>
            <FormattedMessage id={MSG.updatedToday.id} defaultMessage={MSG.updatedToday.defaultMessage} />
          </Text>
        </Box>

        <Wrap mt={5} spacing={5}>
          {data?.feed?.listings.map(listing => (
            <ListingCardNew key={listing?.id} listing={listing as ListingCompact} />
          ))}
        </Wrap>
      </Box>

      {!hasNativeAccess && (
        <Box margin={8}>
          {isMediumOrAbove ? (
            <DownloadMobileAppPromptCard isLandingPage={true} />
          ) : (
            <MobileDownloadMobileAppPromptCard isLandingPage={true} />
          )}
        </Box>
      )}

      <HomeSearchFooter />
    </AppLayout>
  )
}
